import { useEffect, useState } from 'react'
import { Send } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import type { ChaveOcorrencia } from './OcorrenciaTextoModal'

// Envio de E-mail da Ocorrência (WOCOEMA) — destinatários de frota.esctm6, todos marcados ao abrir, como no ERP

interface Destinatario { escdpcod: number; escdpff: number; motcod: string; nome: string; email: string }

export default function OcorrenciaEmailModal({ chave, funcionario, tipo, onClose }: {
  chave: ChaveOcorrencia; funcionario: string; tipo: string; onClose: () => void
}) {
  const { empresaCod } = useEmpresa()
  const [lista, setLista] = useState<Destinatario[] | null>(null)
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [enviando, setEnviando] = useState(false)
  const id = (d: Destinatario) => `${d.escdpcod}-${d.escdpff}`

  useEffect(() => {
    api.get('/ocorrencias-funcionarios/email/destinatarios')
      .then(r => { setLista(r.data); setMarcados(new Set((r.data as Destinatario[]).map(id))) })
      .catch(() => setErro('Não foi possível carregar os destinatários.'))
  }, [])

  function alternar(d: Destinatario) {
    setMarcados(s => { const n = new Set(s); if (n.has(id(d))) n.delete(id(d)); else n.add(id(d)); return n })
  }

  async function enviar() {
    if (!lista) return
    const destinos = lista.filter(d => marcados.has(id(d))).map(d => ({ nome: d.nome, email: d.email }))
    if (!destinos.length) { setErro('Marque ao menos um destinatário.'); return }
    setErro(''); setAviso('')
    setEnviando(true)
    try {
      const r = await api.post('/ocorrencias-funcionarios/email', { ...chave, empresa: empresaCod ?? 1, destinos })
      setAviso(`E-mail enviado para ${r.data.enviados} destinatário(s).`)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro no envio do e-mail.')
    } finally {
      setEnviando(false)
    }
  }

  const info = (rot: string, val: string | number) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{rot}</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1b3f7b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(val)}>{val}</div>
    </div>
  )
  const th: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
  const todos = !!lista?.length && marcados.size === lista.length

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)', gap: '1rem' }}>
          <div style={{ minWidth: 0 }}><h2>Envio de E-mail da Ocorrência</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>WOCOEMA</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo" style={{ background: '#f1f5fb', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr) minmax(0, 1fr) 90px', gap: '0.75rem', padding: '0.75rem 1rem',
            background: '#fff', border: '1px solid #c7d6ea', borderRadius: 10, marginBottom: '0.9rem' }}>
            {info('Data', chave.ocodat.split('-').reverse().join('/'))}
            {info('Funcionário', funcionario || '—')}
            {info('Tipo', tipo)}
            {info('Número', chave.oconro)}
          </div>

          {lista === null ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : lista.length === 0 ? (
            <p style={{ color: '#6b7280' }}>Nenhum destinatário cadastrado para e-mail de ocorrências.</p>
          ) : (
            <div style={{ border: '1px solid #bfdbfe', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup><col style={{ width: 44 }} /><col /><col /></colgroup>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'center' }}>
                      <input type="checkbox" checked={todos} title="Marcar/desmarcar todos"
                        onChange={() => setMarcados(todos ? new Set() : new Set(lista.map(id)))} />
                    </th>
                    <th style={th}>Nome</th><th style={th}>E-mail</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((d, i) => (
                    <tr key={id(d)} onClick={() => alternar(d)} style={{ background: i % 2 ? '#f5f9fd' : '#fff', cursor: 'pointer', opacity: marcados.has(id(d)) ? 1 : 0.55 }}>
                      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={marcados.has(id(d))} onChange={() => alternar(d)} onClick={e => e.stopPropagation()} /></td>
                      <td style={{ ...td, fontWeight: 600 }} title={d.nome}>{d.nome}</td>
                      <td style={td} title={d.email}>{d.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {lista && lista.length > 0 && <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 6 }}>{marcados.size} de {lista.length} destinatário(s) marcado(s)</div>}

          {lista !== null && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', fontWeight: 600, margin: '0.8rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={enviar}
            disabled={enviando || !lista?.length || !marcados.size}>
            <Send size={15} /> {enviando ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  )
}
